import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import AppNav, { NAV_SAFE_CLASS } from "@/components/nav/AppNav";
import { Camera, Settings, UserPlus, UserMinus, ArrowLeft, Loader2, Check, X, Mail, MessageSquare } from "lucide-react";

function Avatar({ src, name, size = 96 }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  return (
    <div
      className="rounded-full bg-[#CCFF00] border-hard shadow-hard-sm flex items-center justify-center overflow-hidden font-display shrink-0"
      style={{ width: size, height: size, fontSize: size / 2.4 }}
    >
      {src ? <img src={src} alt={name} className="w-full h-full object-cover" /> : <span>{initial}</span>}
    </div>
  );
}

export default function ProfilePage() {
  const { user: me, logout, setAuthUser } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isSelf = !id || id === me?.id;

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Editable fields (self only)
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [picture, setPicture] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      if (isSelf) {
        const { data } = await api.get("/users/me/settings");
        setProfile({ ...data, is_me: true });
        setName(data.name || "");
        setBio(data.bio || "");
        setPicture(data.picture || null);
      } else {
        const { data } = await api.get(`/users/${id}`);
        setProfile(data);
      }
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      alert("Görsel 4MB'dan büyük olamaz");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = String(reader.result);
      try {
        const { data } = await api.post("/uploads", { data_url: dataUrl, kind: "avatar" });
        setPicture(data.url);
      } catch (err) {
        alert("Yükleme başarısız: " + formatApiError(err));
      }
    };
    reader.readAsDataURL(file);
  };

  const saveProfile = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put("/users/me", { name, bio, picture });
      setProfile({ ...profile, ...data });
      setAuthUser?.({ ...(me || {}), name: data.name, picture: data.picture });
    } catch (e) {
      setError(formatApiError(e));
    }
    setSaving(false);
  };

  const sendFriendReq = async () => {
    try {
      await api.post("/friends/request", { user_id: id });
      await load();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const removeFriend = async () => {
    if (!confirm("Arkadaşlıktan çıkarılsın mı?")) return;
    try {
      await api.delete(`/friends/${id}`);
      await load();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const cancelReq = async () => {
    try {
      await api.delete(`/friends/requests/${profile.outgoing_request_id}`);
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="profile-page">
      <AppNav />
      <div className={NAV_SAFE_CLASS}>
        <div className="max-w-2xl mx-auto p-4 md:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            {!isSelf ? (
              <button
                onClick={() => navigate(-1)}
                data-testid="profile-back-btn"
                className="w-10 h-10 border-hard-2 rounded-xl flex items-center justify-center bg-white press-btn shadow-hard-sm"
                aria-label="Geri"
              >
                <ArrowLeft strokeWidth={3} className="w-4 h-4" />
              </button>
            ) : <div />}
            {isSelf && (
              <Link
                to="/app/settings"
                data-testid="open-settings-btn"
                className="flex items-center gap-2 border-hard-2 rounded-xl px-3 py-2 bg-white press-btn shadow-hard-sm text-xs font-black uppercase tracking-widest"
              >
                <Settings strokeWidth={3} className="w-4 h-4" /> Ayarlar
              </Link>
            )}
          </div>

          {/* Card */}
          <div className="bg-white border-hard shadow-hard rounded-3xl p-5 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <div className="relative w-fit">
                <Avatar src={picture ?? profile?.picture} name={profile?.name} size={112} />
                {isSelf && (
                  <label
                    data-testid="avatar-upload-btn"
                    className="absolute -bottom-1 -right-1 w-10 h-10 bg-[#0A0A0A] text-white rounded-xl border-hard-2 flex items-center justify-center cursor-pointer press-btn shadow-hard-sm"
                    aria-label="Avatar yükle"
                  >
                    <Camera strokeWidth={3} className="w-4 h-4" />
                    <input
                      data-testid="avatar-file-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onPickAvatar}
                    />
                  </label>
                )}
              </div>

              <div className="flex-1 min-w-0">
                {isSelf ? (
                  <>
                    <label className="text-[11px] uppercase font-black tracking-widest text-[#555]">İsim</label>
                    <input
                      data-testid="profile-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full bg-[#F9F8F6] border-hard rounded-xl px-4 py-2.5 mt-1 focus:outline-none focus:bg-white font-display text-2xl"
                    />
                    <div className="mt-3">
                      <label className="text-[11px] uppercase font-black tracking-widest text-[#555]">Bio</label>
                      <textarea
                        data-testid="profile-bio-input"
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        maxLength={280}
                        rows={2}
                        placeholder="Kendini birkaç cümleyle anlat…"
                        className="w-full bg-[#F9F8F6] border-hard rounded-xl px-4 py-2.5 mt-1 focus:outline-none focus:bg-white resize-none"
                      />
                      <div className="text-[10px] text-right text-[#888] mt-0.5">{bio.length}/280</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-display text-3xl">{profile?.name}</div>
                    <div className="flex items-center gap-1 text-xs text-[#666] mt-1">
                      <Mail strokeWidth={2.5} className="w-3.5 h-3.5" /> {profile?.email}
                    </div>
                    {profile?.bio && (
                      <p className="mt-3 text-sm bg-[#F9F8F6] border-hard-2 rounded-xl p-3 whitespace-pre-wrap">{profile.bio}</p>
                    )}
                  </>
                )}
              </div>
            </div>

            {error && (
              <div data-testid="profile-error" className="mt-4 border-hard-2 bg-[#FFD0C2] rounded-xl px-3 py-2 text-sm font-bold">{error}</div>
            )}

            {/* Actions */}
            {isSelf ? (
              <div className="flex flex-wrap gap-2 mt-5">
                <button
                  onClick={saveProfile}
                  disabled={saving}
                  data-testid="profile-save-btn"
                  className="bg-[#CCFF00] text-[#0A0A0A] font-black border-hard shadow-hard rounded-xl px-5 py-2.5 press-btn disabled:opacity-60"
                >
                  {saving ? "Kaydediliyor…" : "Kaydet"}
                </button>
                <button
                  onClick={() => { logout(); navigate("/login"); }}
                  data-testid="profile-logout-btn"
                  className="bg-white border-hard-2 rounded-xl px-5 py-2.5 press-btn font-bold"
                >
                  Çıkış
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 mt-5">
                {profile?.is_friend && (
                  <button
                    onClick={removeFriend}
                    data-testid="profile-remove-friend-btn"
                    className="bg-white border-hard-2 rounded-xl px-4 py-2.5 press-btn font-bold text-sm flex items-center gap-2"
                  >
                    <UserMinus strokeWidth={3} className="w-4 h-4" /> Arkadaşlıktan çıkar
                  </button>
                )}
                {!profile?.is_friend && !profile?.outgoing_request_id && !profile?.incoming_request_id && (
                  <button
                    onClick={sendFriendReq}
                    data-testid="profile-add-friend-btn"
                    className="bg-[#CCFF00] border-hard rounded-xl px-4 py-2.5 press-btn font-black shadow-hard text-sm flex items-center gap-2"
                  >
                    <UserPlus strokeWidth={3} className="w-4 h-4" /> Arkadaş ekle
                  </button>
                )}
                {profile?.outgoing_request_id && (
                  <button
                    onClick={cancelReq}
                    data-testid="profile-cancel-req-btn"
                    className="bg-[#FFF7C2] border-hard-2 rounded-xl px-4 py-2.5 press-btn font-bold text-sm flex items-center gap-2"
                  >
                    <X strokeWidth={3} className="w-4 h-4" /> İsteği iptal et
                  </button>
                )}
                {profile?.incoming_request_id && (
                  <div className="text-xs font-black uppercase tracking-widest bg-[#D4B3FF] border-hard-2 rounded-xl px-3 py-2">
                    <Check strokeWidth={3} className="inline w-3.5 h-3.5 mr-1" /> Sana istek gönderdi
                  </div>
                )}
                {profile?.is_friend && (
                  <Link
                    to="/app/chats"
                    data-testid="profile-message-btn"
                    className="bg-[#D4B3FF] border-hard-2 rounded-xl px-4 py-2.5 press-btn font-bold text-sm flex items-center gap-2"
                  >
                    <MessageSquare strokeWidth={3} className="w-4 h-4" /> Mesaj
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
